/*
 * Based on the Backstage EntityRelationsGraph component. Original code is
 * copyrighted by The Backstage Authors under the Apache License, Version 2.0.
 * This version adds support for visual grouping of related entities.
 */

import React, { ReactNode, useEffect, useMemo } from 'react';
import {
  Entity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  DependencyGraphTypes,
  EmptyState,
} from '@backstage/core-components';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import classNames from 'classnames';
import {
  ALL_RELATION_PAIRS,
  Direction,
  EntityEdge,
  EntityRelationsGraphProps,
} from '@backstage/plugin-catalog-graph';
import { GroupedDependencyGraph, GroupDefinition } from './GroupedDependencyGraph';
import { useEntityRelationNodesAndEdges } from '../../hooks/useEntityRelationNodesAndEdges';
import { ParentGroupDetails } from '../../utils/parentGrouping';

export type GroupedEntityRelationsGraphProps = EntityRelationsGraphProps & {
  getParentGroup?: (entity: Entity) => ParentGroupDetails | undefined;
  groupPadding?: number;
  groupHeaderHeight?: number;
  renderGroupLabel?: (group: GroupDefinition) => ReactNode;
};

export type GroupedEntityRelationsGraphClassKey =
  | 'progress'
  | 'container'
  | 'graph';

const useStyles = makeStyles(
  theme => ({
    progress: {
      position: 'absolute',
      left: '50%',
      top: '50%',
      marginLeft: '-20px',
      marginTop: '-20px',
    },
    container: {
      position: 'relative',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    graph: {
      width: '100%',
      flex: 1,
      '& path[marker-end]': {
        transition: 'filter 0.1s ease-in-out',
      },
      '& path[marker-end]:hover': {
        filter: `drop-shadow(2px 2px 4px ${theme.palette.primary.dark});`,
      },
      '& g[data-testid=label]': {
        transition: 'transform 0s',
      },
    },
  }),
  { name: 'UserStoryGroupedEntityRelationsGraph' },
);

export const GroupedEntityRelationsGraph = (
  props: GroupedEntityRelationsGraphProps,
) => {
  const {
    rootEntityNames,
    maxDepth = 2,
    unidirectional = true,
    mergeRelations = true,
    kinds,
    relations,
    entityFilter,
    direction = Direction.LEFT_RIGHT,
    onNodeClick,
    relationPairs = ALL_RELATION_PAIRS,
    className,
    zoom = 'enabled',
    renderNode,
    renderLabel,
    curve,
    showArrowHeads,
    getParentGroup,
    groupPadding,
    groupHeaderHeight,
    renderGroupLabel,
  } = props;

  const theme = useTheme();
  const classes = useStyles();
  const rootEntityRefs = useMemo(
    () =>
      (Array.isArray(rootEntityNames)
        ? rootEntityNames
        : [rootEntityNames]
      ).map(ref => stringifyEntityRef(ref)),
    [rootEntityNames],
  );
  const errorApi = useApi(errorApiRef);

  const { loading, error, nodes, edges } = useEntityRelationNodesAndEdges({
    rootEntityRefs,
    maxDepth,
    unidirectional,
    mergeRelations,
    kinds,
    relations,
    entityFilter,
    onNodeClick,
    relationPairs,
  });

  useEffect(() => {
    if (error) {
      errorApi.post(error);
    }
  }, [errorApi, error]);

  const groupDefinitions = useMemo<GroupDefinition[]>(() => {
    if (!nodes || !getParentGroup) {
      return [];
    }

    const map = new Map<string, GroupDefinition>();

    nodes.forEach(node => {
      const group = getParentGroup(node.entity);
      if (!group) {
        return;
      }

      const key = group.ref.toLocaleLowerCase('en-US');
      if (!map.has(key)) {
        map.set(key, {
          id: group.ref,
          label: group.label,
          color: group.color,
          members: [],
        });
      }
      map.get(key)!.members.push(node.id);
    });

    return Array.from(map.values()).filter(group => group.members.length > 0);
  }, [getParentGroup, nodes]);

  return (
    <div className={classNames(classes.container, className)}>
      {loading ? <CircularProgress className={classes.progress} /> : null}
      {nodes && edges ? (
        <GroupedDependencyGraph
          nodes={nodes}
          edges={edges}
          renderNode={renderNode}
          renderLabel={renderLabel as DependencyGraphTypes.RenderLabelFunction<EntityEdge> | undefined}
          direction={direction}
          className={classes.graph}
          paddingX={theme.spacing(4)}
          paddingY={theme.spacing(4)}
          labelPosition={DependencyGraphTypes.LabelPosition.RIGHT}
          labelOffset={theme.spacing(1)}
          zoom={zoom}
          curve={curve}
          showArrowHeads={showArrowHeads}
          groups={groupDefinitions}
          groupPadding={groupPadding}
          groupHeaderHeight={groupHeaderHeight}
          renderGroupLabel={renderGroupLabel}
        />
      ) : null}
      {!loading && !nodes?.length ? (
        <EmptyState missing="content" title="No relations" />
      ) : null}
    </div>
  );
};
